import { Box, Button, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  DataGrid,
  GridCsvExportMenuItem,
  GridFooterContainer,
  GridPagination,
  GridToolbarContainer,
  GridToolbarExportContainer,
} from '@mui/x-data-grid';
import { formatChileanDate } from '../../utils/dates';
import { numberWithPoint } from '../../utils/numbers';
import { portfolioNameAndDetails } from '../../utils/portfolioNameAndDetails';
import Spinner from '../Spinner';

const FundSelectBtn = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  whiteSpace: 'pre-wrap',
  fontSynthesis: 'none',
  fontWeight: theme.typography.button.fontWeight,
  fontSize: theme.typography.button.fontSize,
  letterSpacing: '0px',
  textDecoration: 'none',
  lineHeight: '126.49999856948853%',
  textTransform: 'none',
  margin: '0px',
  backgroundColor: theme.palette.transparent.main,
  color: `${theme.palette.textLight.main} !important`,
  '&:hover': {
    backgroundColor: theme.palette.textLight.main,
    color: `${theme.palette.white.main} !important`,
  },
  '&:disabled': {
    backgroundColor: `${theme.palette.transparent.main} !important`,
    color: `${theme.palette.primary.main} !important`,
    pointerEvents: 'none',
  },
}));

const Divider = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  whiteSpace: 'pre-wrap',
  fontSynthesis: 'none',
  color: theme.palette.primary.dark,
  fontWeight: theme.typography.button.fontWeight,
  fontSize: theme.typography.button.fontSize,
  letterSpacing: '0px',
  textDecoration: 'none',
  lineHeight: '126.49999856948853%',
  textTransform: 'none',
}));

const csvOptions = {
  delimiter: ';',
  valueFormatter: ({ row, field }) => {
    if (field === 'montoDisplay') {
      return row.monto;
    }
    return row[field];
  },
};

function CustomExportButton(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem
        printOptions={{ fileName: 'Descargar informe' }}
        options={csvOptions}
      />
    </GridToolbarExportContainer>
  );
}

function CustomToolbar({ tap, handleTaps, ...props }) {
  const theme = useTheme();
  return (
    <GridToolbarContainer {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <CustomExportButton sx={{ display: { xs: 'none', sm: 'inline-flex', md: 'inline-flex' } }} />
      <Stack direction='row' alignItems='center'>
        <FundSelectBtn
          sx={{
            '&.Mui-disabled': {
              color: theme.palette.primary.main,
            },
          }}
          disabled={tap === 'Fondos'}
          onClick={() => handleTaps('Fondos')}>
          Fondos
        </FundSelectBtn>
        <Divider>|</Divider>
        <FundSelectBtn
          disabled={tap === 'Portfolio'}
          onClick={() => handleTaps('Portfolio')}
          sx={{
            '&.Mui-disabled': {
              color: theme.palette.primary.main,
            },
          }}>
          Portfolios
        </FundSelectBtn>
      </Stack>
    </GridToolbarContainer>
  );
}

const CustomFooterWithExportBtn = () => {
  const theme = useTheme();
  return (
    <GridFooterContainer
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2,
      }}>
      <CustomExportButton
        sx={{
          backgroundColor: 'transparent !important',
          display: { xs: 'inline-flex', sm: 'none', md: 'none' },
          cursor: 'pointer',
          ml: 1,
          boxShadow: 'none',
          border: 'none',
          color: `${theme.palette.textLight.main} !important`,
          '& .MuiSvgIcon-root': {
            color: theme.palette.textLight.main,
          },
        }}
      />
      <GridPagination
        slotProps={{
          select: { native: true },
          actions: { showFirstButton: true, showLastButton: true },
          labelRowsPerPage: 'Filas por página',
        }}
      />
    </GridFooterContainer>
  );
};

function NoRowsOverlay() {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      <Typography sx={{ fontSize: '16px', textAlign: 'center' }}>
        No hay transacciones para mostrar
      </Typography>
    </Stack>
  );
}
export const TableTransactions = ({
  handleTaps,
  tap,
  loadingGetClientTransactionsPortfolio,
  dataTransactions,
  errorGetClientTransactionsPortfolio,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isXsRender = useMediaQuery(theme => theme.breakpoints.only('xs'));
  const column =
    tap === 'Fondos'
      ? [
          {
            field: 'fecha',
            headerName: 'Fecha',
            width: 120,
            flex: '0.5 0 100px',
            type: 'date',
            valueGetter: params => {
              return params.value ? new Date(params.value) : null;
            },
            renderCell: params => (
              <Typography variant='subtitle2'>
                {params.value ? formatChileanDate(params.value) : ''}
              </Typography>
            ),
          },

          {
            field: 'movimiento',
            headerName: 'Movimientos',
            flex: '0.6 0 80px',
            width: 200,
            cellClassName: 'super-app-theme--cell',
          },

          {
            field: 'producto',
            headerName: 'Productos',
            flex: '1 1 150px',
            width: !isXsRender ? 300 : 140,
          },
          {
            field: 'estado',
            headerName: 'Estado',
            flex: '0.6 0 auto',
            width: 160,
          },
          {
            field: 'cuota',
            headerName: 'Cuotas',
            flex: '0.6 0 auto',
            width: 140,
            align: 'right',
            headerAlign: 'right',
            renderCell: params => {
              const value = Number(params.value);
              const { producto, estado, movimiento } = params.row;

              if (producto === 'Caja') {
                return <Typography variant='subtitle2'></Typography>;
              }

              if (estado === 'Pendiente' && movimiento !== 'Rescate') {
                return <Typography variant='subtitle2'>---</Typography>;
              }
              const formatted = value.toFixed(4).replace('.', ',');

              return <Typography variant='subtitle2'>{formatted}</Typography>;
            },
            sortComparator: (v1, v2) =>
              Number.parseFloat(v1.replace(',', '.')) - Number.parseFloat(v2.replace(',', '.')),
          },
          {
            field: 'monto',
            headerName: 'Monto',
            flex: '0.6 0 auto',
            width: 140,
            align: 'right',
            headerAlign: 'right',
            renderCell: params => (
              <Typography variant='subtitle2'>
                {!params.row.montoDisplay || params.row.montoDisplay === '-'
                  ? '---'
                  : `$ ${Number(params.row.montoDisplay.replace(/\./g, '')).toLocaleString(
                      'es-CL',
                    )}`}
              </Typography>
            ),
            valueGetter: params => params.row.monto,
            getExportValue: params => params.row.monto,
          },
        ]
      : [
          {
            field: 'fecha',
            headerName: 'Fecha',
            width: 120,
            flex: '0.5 0 100px',
            hide: true,
            valueGetter: params => {
              const date = params.value;
              if (!date) return '';
              return formatChileanDate(date);
            },
            renderCell: params => <Typography variant='subtitle2'>{params.value}</Typography>,
            getExportValue: params => {
              const date = params.value;
              if (!date) return '';
              return formatChileanDate(date);
            },
          },
          {
            field: 'movimiento',
            headerName: 'Movimientos',
            flex: '0.6 0 80px',
            width: 140,
            cellClassName: 'super-app-theme--cell',
          },
          {
            field: 'portfolioName',
            headerName: 'Nombre de portfolio',
            flex: '1 1 150px',
            width: !isXsRender ? 200 : 200,
          },

          {
            field: 'producto',
            headerName: 'Productos',
            flex: '1 1 150px',
            width: !isXsRender ? 300 : 140,
          },
          {
            field: 'estado',
            headerName: 'Estado',
            flex: '0.6 0 auto',
            width: 124,
          },
          {
            field: 'cuota',
            headerName: 'Cuotas',
            flex: '0.6 0 auto',
            width: 120,
            align: 'right',
            headerAlign: 'right',
            renderCell: params => {
              return (
                <div>
                  <Typography variant='subtitle2'>
                    {params.formattedValue.toFixed(4).replace('.', ',')}
                  </Typography>
                </div>
              );
            },
            sortComparator: (v1, v2) => Number.parseFloat(v1) - Number.parseFloat(v2),
          },
          {
            field: 'monto',
            headerName: 'Monto',
            flex: '0.6 0 auto',
            width: 140,
            align: 'right',
            headerAlign: 'right',
            renderCell: params => (
              <Typography variant='subtitle2'>
                {!params.row.montoDisplay || params.row.montoDisplay === '-'
                  ? '---'
                  : `$ ${Number(params.row.montoDisplay.replace(/\./g, '')).toLocaleString(
                      'es-CL',
                    )}`}
              </Typography>
            ),
            sortComparator: (v1, v2) =>
              Number(v1.replace(/\./g, '')) - Number(v2.replace(/\./g, '')),
          },
        ];

  const generateRowId = () => {
    return `_${Math.random().toString(36).substr(2, 9)}`;
  };

  const optionTransaction = {
    DIVR: 'Dividendo Reinvertido',
    CONT: 'Recarga de caja',
    BUY: 'Aporte',
    SELL: 'Rescate',
    WTHD: 'Retiro de dinero',
    DIV: 'Dividendo pagado',
    ROC: 'Retorno de Capital',
  };
  const row =
    tap === 'Fondos'
      ? dataTransactions
          ?.sort((a, b) => new Date(b.TradeDate) - new Date(a.TradeDate))
          ?.filter(item => item.SubType !== 'LO' && item.SubType !== 'LI')
          ?.map(item => {
            const rawAmount = Number.parseFloat(item.Amount);

            const signedAmount =
              item.SubType === 'WTHD' || item.SubType === 'SELL'
                ? -Math.round(rawAmount)
                : Math.round(rawAmount);

            const montoDisplay = signedAmount !== 0 ? numberWithPoint(signedAmount) : '-';
            return {
              id: generateRowId(),
              fecha: item?.TradeDate,
              movimiento: optionTransaction[item.SubType],
              producto: item.Security_Name,
              estado: item.Status === 4 ? 'Completado' : 'Pendiente',
              cuota: Number.parseFloat(item.Units),
              monto: signedAmount,
              montoDisplay: montoDisplay,
            };
          })
      : dataTransactions
          ?.sort((a, b) => new Date(b.TradeDate) - new Date(a.TradeDate))
          ?.filter(item => item.SubType !== 'LO' && item.SubType !== 'LI' && item.Status !== 0)
          ?.map(item => {
            const fileAs = portfolioNameAndDetails(item.Account_FileAs);
            const rawAmount = Number.parseFloat(item.Amount);

            const signedAmount =
              item.SubType === 'WTHD' || item.SubType === 'SELL'
                ? -Math.round(rawAmount)
                : Math.round(rawAmount);

            const montoDisplay = signedAmount !== 0 ? numberWithPoint(signedAmount) : '-';
            return {
              id: generateRowId(),
              fecha: item?.TradeDate,
              movimiento: optionTransaction[item.SubType],
              portfolioName: fileAs?.fileAsComplete.slice(10),
              producto: item.Security_Name,
              estado: item.Status === 4 ? 'Completado' : 'Pendiente',
              cuota: Number.parseFloat(item.Units),
              monto: signedAmount,
              montoDisplay: montoDisplay,
            };
          });

  if (loadingGetClientTransactionsPortfolio) {
    return <Spinner />;
  }

  if (errorGetClientTransactionsPortfolio) {
    throw new Error('Error component my transactions');
  }
  return (
    <Box
      sx={{
        height: '400px',
        width: { md: '100%' },
        '& .font-tabular-nums': {
          fontVariantNumeric: 'tabular-nums',
        },
      }}>
      <DataGrid
        rowHeight={50}
        columns={column}
        rows={row}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }
        sx={{
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.white.main,
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.smallText.fontSize,
            },
          },
          '.MuiDataGrid-toolbarContaine, .css-128fb87-MuiDataGrid-toolbarContainer': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
          },
          '.MuiDataGrid-toolbarContainer>button': {
            color: theme.palette.textLight.main,
            backgroundColor: theme.palette.white.main,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },

          '.MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '&.MuiDataGrid-root': {
            borderRadius: '8px',
          },
          '.MuiDataGrid-row': {
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.littleText.fontSize,
            },
            textAlign: 'left',
            justifyContent: 'center',
          },

          '& .super-app-theme--cell': {
            width: { md: '100%', xs: '100px' },
            color: theme.palette.primary.main,
            fontWeight: theme.typography.button.fontWeight,
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
          },
          '.MuiDataGrid-footerContainer': {
            fontSize: theme.typography.button.fontSize,
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.typography.smallText.fontSize,
            },
            color: '#ddd',
          },
          '.MuiButtonBase-root': {
            color: theme.palette.white.main,
          },
          '.MuiDataGrid-footerContainer  .MuiButtonBase-root ': {
            color: theme.palette.avatar.border,
            width: { xs: '30px' },
            '&.Mui-disabled': {
              color: theme.palette.textLight.main,
            },
          },
          '& .even-row': {
            backgroundColor: theme.palette.primary.textLightBlue,
          },
          '& .odd-row': {
            backgroundColor: theme.palette.primary.blueSkyInput,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        localeText={{
          toolbarDensity: 'Size',
          toolbarDensityLabel: 'Size',
          toolbarDensityCompact: 'Small',
          toolbarDensityStandard: 'Medium',
          toolbarDensityComfortable: 'Large',
          toolbarExportCSV: 'Exportar a CSV',
          toolbarExport: 'EXPORTAR',
        }}
        slots={{
          toolbar: () => <CustomToolbar tap={tap} handleTaps={handleTaps} />,
          noRowsOverlay: NoRowsOverlay,
          footer: isMobile ? CustomFooterWithExportBtn : undefined,
        }}
        slotProps={{
          pagination: { labelRowsPerPage: 'Filas por página' },
        }}
      />
    </Box>
  );
};
